import { useMemo, forwardRef } from 'react';
import { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import type { OverviewViewMode } from '@/components/editor/dialogs/OverviewDialog';

interface OverviewExportCanvasProps {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  viewMode?: OverviewViewMode;
}

// ─── Shared inline-style helpers ────────────────────────────────────

function topologicalSort(groupBlocks: WorkflowBlock[], internalConnectors: Connector[]): string[] {
  if (groupBlocks.length === 0) return [];
  const adjacency = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  groupBlocks.forEach(b => { adjacency.set(b.id, []); inDegree.set(b.id, 0); });
  internalConnectors.forEach(c => {
    adjacency.get(c.fromId)?.push(c.toId);
    inDegree.set(c.toId, (inDegree.get(c.toId) || 0) + 1);
  });
  const queue = groupBlocks.filter(b => (inDegree.get(b.id) || 0) === 0).map(b => b.id);
  const result: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    result.push(id);
    for (const next of adjacency.get(id) || []) {
      inDegree.set(next, (inDegree.get(next) || 0) - 1);
      if (inDegree.get(next) === 0) queue.push(next);
    }
  }
  groupBlocks.forEach(b => { if (!result.includes(b.id)) result.push(b.id); });
  return result;
}

function ExportBlock({ block }: { block: WorkflowBlock }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 8,
        border: `1px ${block.type === 'condition' ? 'dashed' : 'solid'} ${block.color}`,
        backgroundColor: `${block.color}12`,
        color: block.color,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap' as const,
      }}
    >
      <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.config.name}</span>
    </div>
  );
}

function Arrow() {
  return (
    <svg width="24" height="16" viewBox="0 0 24 16" style={{ flexShrink: 0, opacity: 0.4 }}>
      <line x1="0" y1="8" x2="18" y2="8" stroke="#333" strokeWidth="1.5" />
      <polyline points="14,3 20,8 14,13" fill="none" stroke="#333" strokeWidth="1.5" />
    </svg>
  );
}

function VerticalArrow() {
  return (
    <svg width="16" height="20" viewBox="0 0 16 20" style={{ flexShrink: 0, opacity: 0.4, margin: '0 auto' }}>
      <line x1="8" y1="0" x2="8" y2="14" stroke="#333" strokeWidth="1.5" />
      <polyline points="3,10 8,16 13,10" fill="none" stroke="#333" strokeWidth="1.5" />
    </svg>
  );
}

function GroupHeader({ group, blockCount }: { group: BlockGroup; blockCount: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: group.color, flexShrink: 0 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: group.color }}>{group.name || 'Group'}</span>
      <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>({blockCount} blocks)</span>
    </div>
  );
}

function CrossGroupSection({
  connectors: crossConnectors,
  resolveName,
  resolveColor,
}: {
  connectors: Connector[];
  resolveName: (id: string) => string;
  resolveColor: (id: string) => string;
}) {
  if (crossConnectors.length === 0) return null;
  return (
    <div style={{ borderRadius: 12, border: '2px dashed #ccc', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#888' }}>Cross-entity connections</span>
        <span style={{ fontSize: 11, color: '#888' }}>({crossConnectors.length})</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {crossConnectors.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span style={{ fontWeight: 500, color: resolveColor(c.fromId) }}>{resolveName(c.fromId)}</span>
            <Arrow />
            <span style={{ fontWeight: 500, color: resolveColor(c.toId) }}>{resolveName(c.toId)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── FLAT VIEW (export) ─────────────────────────────────────────────

function FlatGroupSection({
  group, blocks, connectors, children,
}: {
  group: BlockGroup; blocks: WorkflowBlock[]; connectors: Connector[]; children?: React.ReactNode;
}) {
  const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
  const groupBlockIds = new Set(group.blockIds);
  const internal = connectors.filter(c => groupBlockIds.has(c.fromId) && groupBlockIds.has(c.toId));
  const ordered = useMemo(() => topologicalSort(groupBlocks, internal), [groupBlocks, internal]);
  const pairs = new Set(internal.map(c => `${c.fromId}->${c.toId}`));

  return (
    <div style={{ borderRadius: 12, border: `2px solid ${group.color}`, backgroundColor: `${group.color}08`, padding: 16 }}>
      <GroupHeader group={group} blockCount={groupBlocks.length} />
      {ordered.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          {ordered.map((id, i) => {
            const block = groupBlocks.find(b => b.id === id);
            if (!block) return null;
            const prevId = i > 0 ? ordered[i - 1] : null;
            const showArrow = prevId && pairs.has(`${prevId}->${id}`);
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {showArrow && <Arrow />}
                <ExportBlock block={block} />
              </div>
            );
          })}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── DETAILED VIEW (export) ─────────────────────────────────────────

function DetailedExportBlock({ block }: { block: WorkflowBlock }) {
  return (
    <div style={{
      borderRadius: 8,
      border: `1px ${block.type === 'condition' ? 'dashed' : 'solid'} ${block.color}`,
      backgroundColor: `${block.color}08`,
      padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: block.color, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: block.color }}>{block.config.name}</span>
      </div>
      {block.config.description && (
        <p style={{ fontSize: 11, color: '#888', whiteSpace: 'pre-line', marginLeft: 16, lineHeight: 1.5, margin: '0 0 0 16px' }}>
          {block.config.description}
        </p>
      )}
    </div>
  );
}

function DetailedGroupSection({
  group, blocks, connectors, children,
}: {
  group: BlockGroup; blocks: WorkflowBlock[]; connectors: Connector[]; children?: React.ReactNode;
}) {
  const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
  const groupBlockIds = new Set(group.blockIds);
  const internal = connectors.filter(c => groupBlockIds.has(c.fromId) && groupBlockIds.has(c.toId));
  const ordered = useMemo(() => topologicalSort(groupBlocks, internal), [groupBlocks, internal]);
  const pairs = new Set(internal.map(c => `${c.fromId}->${c.toId}`));

  return (
    <div style={{ borderRadius: 12, border: `2px solid ${group.color}`, backgroundColor: `${group.color}06`, padding: 16 }}>
      <GroupHeader group={group} blockCount={groupBlocks.length} />
      {ordered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {ordered.map((id, i) => {
            const block = groupBlocks.find(b => b.id === id);
            if (!block) return null;
            const prevId = i > 0 ? ordered[i - 1] : null;
            const showArrow = prevId && pairs.has(`${prevId}->${id}`);
            return (
              <div key={id} style={{ display: 'flex', flexDirection: 'column' }}>
                {showArrow && <VerticalArrow />}
                <DetailedExportBlock block={block} />
              </div>
            );
          })}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── TREE VIEW (export) ─────────────────────────────────────────────

function TreeExportView({
  blocks, connectors, groups,
  crossGroupConnectors, resolveName, resolveColor,
}: {
  blocks: WorkflowBlock[]; connectors: Connector[]; groups: BlockGroup[];
  crossGroupConnectors: Connector[];
  resolveName: (id: string) => string; resolveColor: (id: string) => string;
}) {
  const topLevelGroups = groups.filter(g => !g.parentGroupId);
  const childGroupsOf = (parentId: string) => groups.filter(g => g.parentGroupId === parentId);
  const allGroupedBlockIds = useMemo(() => new Set(groups.flatMap(g => g.blockIds)), [groups]);
  const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));
  const groupIds = useMemo(() => new Set(groups.map(g => g.id)), [groups]);
  const blockConnectors = connectors.filter(c => !groupIds.has(c.fromId) && !groupIds.has(c.toId));

  function getGroupBlocks(group: BlockGroup) {
    const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
    const groupBlockIds = new Set(group.blockIds);
    const internal = blockConnectors.filter(c => groupBlockIds.has(c.fromId) && groupBlockIds.has(c.toId));
    return topologicalSort(groupBlocks, internal).map(id => groupBlocks.find(b => b.id === id)!).filter(Boolean);
  }

  function renderTreeGroup(group: BlockGroup, depth: number): React.ReactNode {
    const children = childGroupsOf(group.id);
    const groupBlocks = getGroupBlocks(group);
    return (
      <div key={group.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: depth * 20 + 8, paddingTop: 4, paddingBottom: 4 }}>
          <span style={{ fontSize: 10, color: '#888' }}>▼</span>
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: group.color, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: group.color }}>{group.name || 'Group'}</span>
          <span style={{ fontSize: 11, color: '#888' }}>({groupBlocks.length} blocks)</span>
        </div>
        <div style={{ borderLeft: `2px solid ${group.color}40`, marginLeft: depth * 20 + 18 }}>
          {groupBlocks.map(block => (
            <div key={block.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 12px', fontSize: 12 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: block.color, flexShrink: 0 }} />
              <span style={{ fontWeight: 500, color: block.color }}>{block.config.name}</span>
              {block.config.description && (
                <span style={{ color: '#999', fontSize: 11 }}>— {block.config.description.split('\n')[0]}</span>
              )}
            </div>
          ))}
          {children.map(child => renderTreeGroup(child, depth + 1))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ borderRadius: 12, border: '1px solid #ddd', padding: 8 }}>
        {topLevelGroups.map(g => renderTreeGroup(g, 0))}
        {ungroupedBlocks.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', fontSize: 13, fontWeight: 600, color: '#888' }}>
              Root (ungrouped) <span style={{ fontSize: 11, fontWeight: 400 }}>({ungroupedBlocks.length} blocks)</span>
            </div>
            {ungroupedBlocks.map(block => (
              <div key={block.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 12px', marginLeft: 20, fontSize: 12 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: block.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 500, color: block.color }}>{block.config.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <CrossGroupSection connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
    </div>
  );
}

// ─── COMPACT GRID VIEW (export) ─────────────────────────────────────

function CompactGridExportView({
  blocks, groups,
  crossGroupConnectors, resolveName, resolveColor,
}: {
  blocks: WorkflowBlock[]; groups: BlockGroup[];
  crossGroupConnectors: Connector[];
  resolveName: (id: string) => string; resolveColor: (id: string) => string;
}) {
  const topLevelGroups = groups.filter(g => !g.parentGroupId);
  const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
  const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {topLevelGroups.map(group => {
          const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
          return (
            <div key={group.id} style={{ borderRadius: 12, border: `2px solid ${group.color}`, backgroundColor: `${group.color}08`, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: group.color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: group.color }}>{group.name || 'Group'}</span>
              </div>
              <span style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 8 }}>{groupBlocks.length} blocks</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {groupBlocks.slice(0, 6).map(b => (
                  <span key={b.id} style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `1px solid ${b.color}40`,
                    color: b.color, backgroundColor: `${b.color}10`,
                  }}>
                    {b.config.name}
                  </span>
                ))}
                {groupBlocks.length > 6 && (
                  <span style={{ fontSize: 10, padding: '2px 6px', color: '#888' }}>+{groupBlocks.length - 6} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {ungroupedBlocks.length > 0 && (
        <div style={{ borderRadius: 12, border: '2px dashed #aaa', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#888' }}>Root (ungrouped)</span>
            <span style={{ fontSize: 11, color: '#888' }}>({ungroupedBlocks.length} blocks)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {ungroupedBlocks.map(b => (
              <span key={b.id} style={{
                fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `1px solid ${b.color}40`,
                color: b.color, backgroundColor: `${b.color}10`,
              }}>
                {b.config.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <CrossGroupSection connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
    </div>
  );
}

// ─── MAIN EXPORT CANVAS ─────────────────────────────────────────────

export const OverviewExportCanvas = forwardRef<HTMLDivElement, OverviewExportCanvasProps>(
  ({ blocks, connectors, groups, viewMode = 'flat' }, ref) => {
    const groupIds = useMemo(() => new Set(groups.map(g => g.id)), [groups]);
    const blockConnectors = connectors.filter(c => !groupIds.has(c.fromId) && !groupIds.has(c.toId));

    const crossGroupConnectors = useMemo(() => {
      return connectors.filter(c => groupIds.has(c.fromId) || groupIds.has(c.toId));
    }, [connectors, groupIds]);

    const resolveName = (id: string) => {
      const group = groups.find(g => g.id === id);
      if (group) return group.name || 'Group';
      const block = blocks.find(b => b.id === id);
      if (block) return block.config.name;
      return id;
    };

    const resolveColor = (id: string) => {
      const group = groups.find(g => g.id === id);
      if (group) return group.color;
      const block = blocks.find(b => b.id === id);
      if (block) return block.color;
      return '#333';
    };

    const topLevelGroups = groups.filter(g => !g.parentGroupId);
    const childGroupsOf = (parentId: string) => groups.filter(g => g.parentGroupId === parentId);
    const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
    const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));

    function renderFlatGroupTree(group: BlockGroup, depth: number): React.ReactNode {
      const children = childGroupsOf(group.id);
      return (
        <div key={group.id} style={{ marginLeft: depth > 0 ? 16 : 0 }}>
          <FlatGroupSection group={group} blocks={blocks} connectors={blockConnectors}>
            {children.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {children.map(child => renderFlatGroupTree(child, depth + 1))}
              </div>
            )}
          </FlatGroupSection>
        </div>
      );
    }

    function renderDetailedGroupTree(group: BlockGroup, depth: number): React.ReactNode {
      const children = childGroupsOf(group.id);
      return (
        <div key={group.id} style={{ marginLeft: depth > 0 ? 16 : 0 }}>
          <DetailedGroupSection group={group} blocks={blocks} connectors={blockConnectors}>
            {children.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {children.map(child => renderDetailedGroupTree(child, depth + 1))}
              </div>
            )}
          </DetailedGroupSection>
        </div>
      );
    }

    function renderContent() {
      switch (viewMode) {
        case 'detailed':
          return (
            <>
              {topLevelGroups.map(g => renderDetailedGroupTree(g, 0))}
              {ungroupedBlocks.length > 0 && (
                <div style={{ borderRadius: 12, border: '2px dashed #aaa', padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#888' }}>Root (ungrouped)</span>
                    <span style={{ fontSize: 11, color: '#888' }}>({ungroupedBlocks.length} blocks)</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ungroupedBlocks.map(b => <DetailedExportBlock key={b.id} block={b} />)}
                  </div>
                </div>
              )}
              <CrossGroupSection connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
            </>
          );

        case 'tree':
          return (
            <TreeExportView
              blocks={blocks}
              connectors={connectors}
              groups={groups}
              crossGroupConnectors={crossGroupConnectors}
              resolveName={resolveName}
              resolveColor={resolveColor}
            />
          );

        case 'compact':
          return (
            <CompactGridExportView
              blocks={blocks}
              groups={groups}
              crossGroupConnectors={crossGroupConnectors}
              resolveName={resolveName}
              resolveColor={resolveColor}
            />
          );

        case 'flat':
        default:
          return (
            <>
              {topLevelGroups.map(g => renderFlatGroupTree(g, 0))}
              {ungroupedBlocks.length > 0 && (
                <div style={{ borderRadius: 12, border: '2px dashed #aaa', padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#888' }}>Root (ungrouped)</span>
                    <span style={{ fontSize: 11, color: '#888' }}>({ungroupedBlocks.length} blocks)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {ungroupedBlocks.map(b => <ExportBlock key={b.id} block={b} />)}
                  </div>
                </div>
              )}
              <CrossGroupSection connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
            </>
          );
      }
    }

    return (
      <div
        ref={ref}
        style={{
          backgroundColor: '#ffffff',
          color: '#111',
          padding: 32,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          minWidth: 600,
          maxWidth: 1200,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {renderContent()}
        </div>
      </div>
    );
  }
);

OverviewExportCanvas.displayName = 'OverviewExportCanvas';
